import { useState, useCallback, useEffect } from 'react';

// ============================================================================
// TIPOS
// ============================================================================

interface HapticFeedbackOptions {
  enableHaptic?: boolean;
  enableSound?: boolean;
  enableVibration?: boolean;
  soundVolume?: number;
  vibrationIntensity?: 'light' | 'medium' | 'heavy';
}

// interface HapticPattern {
//   type: 'success' | 'error' | 'warning' | 'info' | 'selection' | 'impact';
//   intensity?: 'light' | 'medium' | 'heavy';
//   duration?: number;
//   pattern?: number[];
// }

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export const useHapticFeedback = (options: HapticFeedbackOptions = {}) => {
  const {
    enableSound = false,
    enableVibration = true,
    soundVolume = 0.5
  } = options;

  const [isSupported, setIsSupported] = useState(false);

  // Detectar soporte para haptic feedback
  useEffect(() => {
    const checkSupport = () => {
      const hasVibration = 'vibrate' in navigator;
      const hasHaptic = 'haptic' in navigator;
      const hasAudio = 'AudioContext' in window || 'webkitAudioContext' in window;
      
      setIsSupported(hasVibration || hasHaptic || hasAudio);
    };

    checkSupport();
  }, []);

  // Vibrar dispositivo
  const vibrate = useCallback((pattern: number | number[]) => {
    if (!enableVibration || !isSupported) return;

    try {
      if ('vibrate' in navigator) {
        navigator.vibrate(pattern);
      }
    } catch (error) {
      console.warn('Vibration not supported:', error);
    }
  }, [enableVibration, isSupported]);

  // Reproducir sonido
  const playSound = useCallback((frequency: number, duration: number) => {
    if (!enableSound) return;

    try {
      const audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      gainNode.gain.setValueAtTime(soundVolume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration / 1000);
    } catch (error) {
      console.warn('Audio not supported:', error);
    }
  }, [enableSound, soundVolume]);

  // Métodos específicos para diferentes tipos de feedback
  const successFeedback = useCallback(async () => {
    vibrate([50, 50, 50]);
    playSound(800, 100);
  }, [vibrate, playSound]);

  const errorFeedback = useCallback(async () => {
    vibrate([100, 50, 100]);
    playSound(200, 300);
  }, [vibrate, playSound]);

  const warningFeedback = useCallback(async () => {
    vibrate([75, 25, 75]);
    playSound(400, 150);
  }, [vibrate, playSound]);

  const infoFeedback = useCallback(async () => {
    vibrate([25]);
    playSound(600, 50);
  }, [vibrate, playSound]);

  const selectionFeedback = useCallback(async () => {
    vibrate([10]);
    playSound(500, 20);
  }, [vibrate, playSound]);

  const impactFeedback = useCallback(async () => {
    vibrate([50]);
    playSound(300, 100);
  }, [vibrate, playSound]);

  return {
    isSupported,
    successFeedback,
    errorFeedback,
    warningFeedback,
    infoFeedback,
    selectionFeedback,
    impactFeedback,
    vibrate,
    playSound
  };
};

export default useHapticFeedback;